/* sidePanelClearance.js — a SIDE PANEL IS REAL GEOMETRY (2026-09-07 user:
 * "the side panel seems like it doesn't have physics, it goes through the wall
 * or through the cabinets when you get close — fix that").
 *
 * A decor end panel is per-PLACEMENT config (config.sidePanel + sidePanelPos,
 * a set string over "L R T B K", with per-side overrides in
 * sidePanelSideCfg). The renderer already seats each board OUTSIDE the
 * carcass; nothing else in the engine knew it existed, so collision, the drag
 * clamps, the magnets and the wall SAT all measured the bare cabinet and let
 * the board pass through walls and neighbours.
 *
 * This module turns that config into EXTENTS in metres — how far the module
 * really reaches beyond its catalog box on each side — exactly mirroring the
 * geometry cabinetMath.js draws:
 *   vertical board: seated at (carcassW/2 + t/2) where carcassW = width − 8mm,
 *                   so its outer face is (width/2 − 4mm + t)  → extent t − 4mm
 *   top/bottom cap: sits fully above / below the module     → extent t
 * A per-side Z nudge that pushes a board further outboard is carried too.
 *
 * THICKNESS lives in the technician catalog (blob.sidePanels[].thicknessMm),
 * which the engine layer never sees — so the catalog registers it here once
 * (buildModuleLibrary), the same singleton pattern pricingSettings.js uses.
 * An unregistered entry still counts as physical at the 18 mm default: a panel
 * is never silently weightless.
 */
import { resolveTok } from "./catalogIds";

export const SP_DEFAULT_T_M = 0.018;   // the catalog default (18 mm)
export const SP_MIN_T_M = 0.006;       // the same floor the renderer and the pricer use
const CARCASS_SIDE_INSET_M = 0.004;    // cabinetMath: carcassW = def.width − 0.008
const SIDES = ["L", "R", "T", "B"];    // K (back) never widens the along-wall footprint

let SP_LIST = [];                      // [{ uid, tM }] — mirrors blob.sidePanels order

/** register the catalog's panel thicknesses (called from buildModuleLibrary) */
export function registerSidePanels(blob) {
  const rows = (blob && blob.sidePanels) || [];
  SP_LIST = (Array.isArray(rows) ? rows : []).map((e) => ({
    uid: e && e.uid,
    tM: Math.max(SP_MIN_T_M, (Number(e && e.thicknessMm) || 18) / 1000),
  }));
  return SP_LIST.length;
}

/** thickness (m) for a config ref: "sp-u7" | "sp-3" | "u7" | "3" */
export function sidePanelThicknessOf(ref) {
  const raw = String(ref == null ? "" : ref).trim();
  if (!raw || raw === "None") return 0;
  const m = /^sp-(.+)$/.exec(raw);
  const k = resolveTok(SP_LIST, m ? m[1] : raw);
  return k >= 0 ? SP_LIST[k].tM : SP_DEFAULT_T_M;
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** which sides carry a board, and with which catalog entry */
export function sidePanelSidesOf(p) {
  const out = { L: null, R: null, T: null, B: null };
  const cfg = (p && p.config) || {};
  const base = String(cfg.sidePanel || "");
  const pos = String(cfg.sidePanelPos || "");
  const per = cfg.sidePanelSideCfg || {};
  SIDES.forEach((s) => {
    if (pos.indexOf(s) < 0) return;
    const sc = per[s] || {};
    const ref = sc.sp ? "sp-" + sc.sp : base;
    if (!ref || ref === "None") return;
    out[s] = ref;
  });
  return out;
}

/** how far `p` reaches beyond its catalog box, in metres, per side */
export function sidePanelExtents(p, def) {
  const out = { left: 0, right: 0, top: 0, bottom: 0 };
  if (!p || !def) return out;
  const sides = sidePanelSidesOf(p);
  if (!sides.L && !sides.R && !sides.T && !sides.B) return out;
  const cfg = p.config || {};
  const per = cfg.sidePanelSideCfg || {};
  const zOf = (s) => {
    const sc = per[s] || {};
    return (sc.z != null ? num(sc.z) : num(cfg.sidePanelZmm)) / 1000;   // + = toward the R side
  };
  const boardOut = (ref) => Math.max(0, sidePanelThicknessOf(ref) - CARCASS_SIDE_INSET_M);
  if (sides.L) out.left = Math.max(0, boardOut(sides.L) - zOf("L"));    // a +Z nudge pulls the L board inboard
  if (sides.R) out.right = Math.max(0, boardOut(sides.R) + zOf("R"));
  if (sides.T) out.top = sidePanelThicknessOf(sides.T);                  // the cap stands on top of the module
  if (sides.B) out.bottom = sidePanelThicknessOf(sides.B);
  return out;
}

/** true when this placement has any panel that occupies space */
export function hasSidePanels(p, def) {
  const e = sidePanelExtents(p, def);
  return !!(e.left || e.right || e.top || e.bottom);
}

/** the along-wall span the module really occupies, relative to absLeft */
export function panelSpanOf(p, def) {
  const e = sidePanelExtents(p, def);
  const w = (def && def.width) || 0;
  return { lo: e.left ? -e.left : 0, hi: w + e.right, ext: e };
}

/** the panel material standing between two SAME-WALL modules, a on the LEFT of b (m) */
export function pairPanelGap(aLeft, defA, bRight, defB) {
  return sidePanelExtents(aLeft, defA).right + sidePanelExtents(bRight, defB).left;
}

/** panel-aware pose for the collision primitives: the box grows on the panel
 *  side(s) and its centre shifts so the CARCASS stays exactly where it is. */
export function panelPose(p, def, pos) {
  const e = sidePanelExtents(p, def);
  const shift = (e.right - e.left) / 2;
  const ca = Math.cos((pos && pos.angle) || 0), sa = Math.sin((pos && pos.angle) || 0);
  return {
    cx: (pos ? pos.cx : 0) + ca * shift,     // width axis u = (cos a, −sin a) → +u is the R side
    cz: (pos ? pos.cz : 0) - sa * shift,
    halfW: ((def && def.width) || 0) / 2 + (e.left + e.right) / 2,
    ext: e,
    shift,
  };
}
